import { slotOf, slotOfMany, reprogram, hurt, } from './movement.js';
import { discardCards, drawCards, shuffle } from './cards.js';
import { cardHolders, EXTRA_REGISTER, holdsOption, isMoveOption, isPairingOption, NEXT_REGISTER_WEAPONS, pairProblem, PUSH_OPTIONS, REGISTER_WEAPONS, SIDED_TURN_CARDS, TURN_WEAPONS, } from './options.js';
/**
 * STATUS: real, complete for the base game. Turn step 2 of 5 --
 * RULES_SPEC.md section 2's `Program Registers`.
 *
 * Timer expiry: the tabletop rule has the player to the right shuffle the
 * unused cards and deal them into the empty registers, but since a
 * computer is running the game it draws randomly from that player's own
 * hand itself. A partially-programmed player keeps every register they
 * filled and only the empty ones are filled at random; a player who
 * programmed nothing gets all five filled from their whole hand.
 *
 * A locked register is not part of the submission at all -- it already
 * holds the card it will execute again this turn (placed either by last
 * turn's programming or, if it was empty, by the Deal step's random fill).
 * Submitting anything for a locked register is rejected rather than
 * silently ignored, since a client doing that is confused about the game
 * state and should be told.
 */
/** What counts as a movement card, which the Flywheel requires. */
const MOVEMENT_CARDS = new Set(['Move1', 'Move2', 'Move3', 'BackUp']);
/** The four sides of a robot, which an aimed Option is turned to. Checked
 * rather than trusted: a facing arrives off the wire as a string. */
export const RELATIVE_FACINGS = ['front', 'back', 'left', 'right'];
/** And the four a robot can point, for the starting facing. Same
 * reasoning: it arrives off the wire as a string. */
const FACINGS = new Set(['N', 'E', 'S', 'W']);
const isCard = (c) => (typeof c === 'object' && c !== null
    && typeof c.priority === 'number' && typeof c.type === 'string');
const isIdList = (v) => Array.isArray(v) && v.every((id) => typeof id === 'string');
const isListOf = (v, each) => (v === undefined || (Array.isArray(v) && v.every(each)));
/** What is malformed about a submission, before anything reads into it. */
function shapeProblems(s) {
    const wrong = [];
    if (!Array.isArray(s.registers) || !s.registers.every((c) => c === null || isCard(c))) {
        wrong.push('registers must be cards or empty');
    }
    if (!isListOf(s.paired, (p) => p === null || (typeof p === 'object'
        && typeof p.via === 'string'
        && (p.card === undefined || isCard(p.card))))) {
        wrong.push('paired registers must name an Option and a card');
    }
    if (!isListOf(s.gadgets, isIdList))
        wrong.push('gadgets must be lists of Option ids');
    if (!isListOf(s.moveOptions, isIdList))
        wrong.push('move options must be lists of Option ids');
    if (s.activated !== undefined && !isIdList(s.activated)) {
        wrong.push('activated must be a list of Option ids');
    }
    if (!isListOf(s.aimed, (a) => typeof a === 'object' && a !== null
        && typeof a.optionId === 'string'
        && typeof a.facing === 'string')) {
        wrong.push('aimed must name an Option and a side');
    }
    if (!isListOf(s.setAside, (e) => typeof e === 'object' && e !== null
        && typeof e.optionId === 'string'
        && isCard(e.card))) {
        wrong.push('a card set aside must name an Option and a card');
    }
    return wrong;
}
/**
 * Whether a robot virtual as it programs is certain to be virtual still
 * in a register, by index, so a launcher there can only fizzle.
 *
 * Register 1 always: being virtual ends only at the end of a register.
 * On turn 1 every register, since a robot virtual then stays virtual for
 * the whole turn. Past that, it may separate, so the rest are left open.
 */
export function virtualRegisters(robot, turnNumber) {
    if (!robot.virtual)
        return () => false;
    return turnNumber === 1 ? () => true : (register) => register === 0;
}
/** Pure check, exported so a server can validate a submission the moment it
 * arrives rather than waiting for the whole table. Returns an empty array
 * when the submission is acceptable. */
export function validateSubmission(robot, submission, 
/** The turn, where known: a facing goes with the program only on the
 * robot's first, on turn 1 or the turn it joined. */
turnNumber) {
    // The shape first. A submission arrives off the wire and is parked as
    // sent, and everything below reads into it: one that throws here throws
    // in every advance that carries it, and blocks the turn for the table.
    const malformed = shapeProblems(submission);
    if (malformed.length > 0)
        return malformed;
    const problems = [];
    // A robot back from destruction is turned and gives up an Option, and
    // one asked whether it is powered down answers, before its hand is seen.
    if (robot.owesFacing)
        problems.push('choose a facing before programming');
    if (robot.owesOptionLoss)
        problems.push('choose an Option to give up before programming');
    if (robot.owesPowerChoice)
        problems.push('choose whether to power down before programming');
    if (submission.facing !== undefined && turnNumber !== undefined
        && turnNumber !== 1 && robot.joinedTurn !== turnNumber) {
        problems.push('a facing goes with the program only on a robot\'s first turn');
    }
    // Five, or six with Extra Register's below.
    const count = submission.registers.length;
    if (count !== 5 && count !== EXTRA_REGISTER) {
        problems.push(`expected 5 register slots, got ${count}`);
        return problems; // nothing else is meaningful against a malformed array
    }
    // Checked rather than trusted, like the aimed facings below: this one
    // arrives off the wire as a string too and is written STRAIGHT onto the
    // robot, so an unchecked value leaves it pointing somewhere that is not
    // a direction, and every reader of `facing` downstream then has to cope
    // with it.
    if (submission.facing !== undefined && !FACINGS.has(submission.facing)) {
        problems.push(`${submission.facing} is not a direction`);
    }
    const locked = robot.lockedRegisters ?? [false, false, false, false, false];
    const hand = robot.hand ?? [];
    const handPriorities = new Set(hand.map((c) => c.priority));
    const used = new Set();
    submission.registers.forEach((card, i) => {
        if (locked[i]) {
            if (card !== null)
                problems.push(`register ${i + 1} is locked and cannot be programmed`);
            return;
        }
        if (card === null)
            return;
        if (!handPriorities.has(card.priority)) {
            problems.push(`card with priority ${card.priority} is not in this robot's hand`);
        }
        if (used.has(card.priority)) {
            problems.push(`card with priority ${card.priority} was programmed into two registers`);
        }
        used.add(card.priority);
    });
    // Paired registers: the Option held and one that pairs, the register
    // open, the cards a pair it takes, and the second card from the hand
    // like the first.
    const paired = submission.paired ?? [];
    if (paired.length > count)
        problems.push(`there are only ${count} registers to pair cards in`);
    paired.slice(0, count).forEach((pair, i) => {
        if (!pair)
            return;
        const option = (robot.options ?? []).find((o) => o.id === pair.via);
        if (!option) {
            problems.push(`no option ${pair.via} to program`);
            return;
        }
        if (!isPairingOption(option)) {
            problems.push(`${pair.via} does not pair cards`);
            return;
        }
        if (locked[i]) {
            problems.push(`register ${i + 1} is locked and cannot be paired`);
            return;
        }
        const first = submission.registers[i];
        const cards = [first, pair.card].filter((c) => c != null);
        const wrong = pairProblem(option, cards);
        if (wrong)
            problems.push(`register ${i + 1}: ${wrong}`);
        if (pair.card) {
            if (!handPriorities.has(pair.card.priority)) {
                problems.push(`card with priority ${pair.card.priority} is not in this robot's hand`);
            }
            if (used.has(pair.card.priority)) {
                problems.push(`card with priority ${pair.card.priority} was programmed into two registers`);
            }
            used.add(pair.card.priority);
        }
        if (option.effect === 'overloadOverride' && (submission.gadgets?.[i] ?? []).length > 0) {
            problems.push(`a gadget cannot go in register ${i + 1}, which is programmed with ${option.name}`);
        }
        // No manoeuvre changes a paired register. The Big Gun changes what
        // the robot fires, not its cards, so it still goes beside a pair.
        const moving = (submission.moveOptions?.[i] ?? []).filter((id) => {
            const held = (robot.options ?? []).find((o) => o.id === id);
            return !held || !REGISTER_WEAPONS.has(held.effect);
        });
        if (moving.length > 0) {
            problems.push(`a move option cannot go in register ${i + 1}, which holds a pair`);
        }
    });
    // Enough cards to finish. A pair spends two and only Overload Override
    // may leave a register empty, so a part-programmed robot must still have
    // the cards for every register it has not filled -- or the timer would
    // have nothing to fill them from.
    const needed = Array.from({ length: count }, (_, i) => i).reduce((n, i) => {
        if (locked[i])
            return n;
        // Register 6 needs no card: left empty, it is not played.
        if (i === EXTRA_REGISTER - 1 && !submission.registers[i] && !paired[i])
            return n;
        const pair = paired[i];
        if (!pair)
            return n + 1;
        return n + (pair.card ? 2 : submission.registers[i] ? 1 : 0);
    }, 0) + (submission.setAside ?? []).length;
    if (paired.some((p) => p) && needed > hand.length) {
        problems.push(`this program needs ${needed} cards and the hand holds ${hand.length}`);
    }
    // A launcher or a register weapon where the robot is still virtual
    // could only fizzle.
    const stillVirtual = virtualRegisters(robot, turnNumber);
    if (submission.gadgets) {
        const byId = new Map((robot.options ?? []).map((o) => [o.id, o]));
        // How many registers each gadget was put into.
        const timesProgrammed = new Map();
        submission.gadgets.forEach((ids, i) => {
            if (ids.length > 1) {
                problems.push(`only one gadget can be programmed into register ${i + 1}`);
            }
            if (ids.length > 0 && stillVirtual(i)) {
                problems.push(`register ${i + 1}: your robot is still virtual then and drops nothing`);
            }
            for (const id of ids) {
                if (!byId.has(id)) {
                    problems.push(`no option ${id} to program`);
                    continue;
                }
                timesProgrammed.set(id, (timesProgrammed.get(id) ?? 0) + 1);
            }
        });
        // A dropper or other ammo option can be programmed into MORE THAN ONE
        // register in a turn. What limits it is the ammo it has left, not the
        // register count -- three shots left is three registers. This used to
        // reject any second register outright, on the reasoning that a
        // launcher fires once per firing, which conflated one shot per
        // REGISTER with one shot per TURN.
        for (const [id, count] of timesProgrammed) {
            const option = byId.get(id);
            if (count === 1)
                continue;
            // An option with no ammo at all is a one-shot: it has nothing to
            // count down, so it goes in exactly one register.
            if (option.payloadKind !== 'ammo') {
                problems.push(`${id} cannot be programmed into two registers`);
                continue;
            }
            const left = option.remaining ?? 0;
            if (count > left) {
                problems.push(`${id} is programmed into ${count} registers but has ${left} left`);
            }
        }
    }
    // Register 6: with Extra Register, a card in it, and the five before it
    // programmed -- a card, a pair, or Overload Override's none. Anything
    // else put there with no card is refused rather than dropped.
    if (count === EXTRA_REGISTER) {
        const i = EXTRA_REGISTER - 1;
        const anything = submission.registers[i] !== null || paired[i]
            || (submission.gadgets?.[i] ?? []).length > 0
            || (submission.moveOptions?.[i] ?? []).length > 0;
        if (anything) {
            const programmed = (j) => locked[j] || submission.registers[j] !== null
                || (paired[j] != null && !paired[j].card);
            if (!holdsOption(robot.options, 'extraRegister')) {
                problems.push('register 6 needs Extra Register');
            }
            else if (submission.registers[i] === null) {
                problems.push('register 6 needs a card');
            }
            else if (![0, 1, 2, 3, 4].every(programmed)) {
                problems.push('registers 1 to 5 must be programmed before register 6');
            }
        }
    }
    // Turn-programmed Options: the robot must hold the card, and the card
    // must actually be turn-programmed. A phase-programmed launcher goes in
    // a register (`gadgets`) and a run-time card is answered at the
    // register that offers it, so naming either here is a stale or
    // confused client rather than a play.
    for (const id of submission.activated ?? []) {
        const held = (robot.options ?? []).find((o) => o.id === id);
        if (!held) {
            problems.push(`no option ${id} to activate`);
            continue;
        }
        if (held.timing !== 'turnProgrammed') {
            problems.push(`${id} is not a turn-programmed option`);
        }
        // An aimed Option is turned, not switched on. It is already working;
        // activating it would be a claim the engine has nowhere to put.
        if (held.directional) {
            problems.push(`${id} is aimed rather than activated`);
        }
        // Nor is a card-holder switched on. The Flywheel is turn-programmed --
        // it is set up while programming, for a later turn -- but what it
        // takes is a CARD, through `setAside`, and its held card is dealt back
        // automatically at the next Deal. Nothing reads an activation for it,
        // so accepting one recorded a switch that does nothing: rejected here
        // for the same reason a directional card is, and `cardHolders` is the
        // one definition both the engine and the client answer to.
        if (cardHolders([held]).length > 0) {
            problems.push(`${id} holds a card rather than being switched on`);
        }
        // A Transverse Beam or a Shunt is switched on with its side.
        if (SIDED_TURN_CARDS.has(held.effect)
            && !(submission.aimed ?? []).some((a) => a.optionId === id && (a.facing === 'left' || a.facing === 'right'))) {
            problems.push(`${id} needs a side, left or right`);
        }
        // Extra Register is used by programming register 6.
        if (held.effect === 'extraRegister') {
            problems.push(`${id} is used by programming register 6`);
        }
    }
    // Move options: held, a move option, and one per register.
    (submission.moveOptions ?? []).forEach((ids, i) => {
        if (i >= count) {
            problems.push(`there are only ${count} registers to program move options into`);
            return;
        }
        const moving = ids.filter((id) => {
            const held = (robot.options ?? []).find((o) => o.id === id);
            return !held || !REGISTER_WEAPONS.has(held.effect);
        });
        if (moving.length > 1)
            problems.push(`only one move option can go in register ${i + 1}`);
        // The Big Gun and the EMP Generator both fire instead of the main laser.
        const weapons = ids.filter((id) => {
            const held = (robot.options ?? []).find((o) => o.id === id);
            return held !== undefined && REGISTER_WEAPONS.has(held.effect);
        });
        if (weapons.length > 1)
            problems.push(`only one weapon can go in register ${i + 1}`);
        if (weapons.length > 0 && stillVirtual(i)) {
            problems.push(`register ${i + 1}: your robot is still virtual then and fires nothing`);
        }
        // The EMP Generator acts on the next register, and after the fifth
        // there is none -- Extra Register's sixth is no one else's.
        for (const id of ids) {
            const held = (robot.options ?? []).find((o) => o.id === id);
            if (held && i >= 4 && NEXT_REGISTER_WEAPONS.has(held.effect)) {
                problems.push(`${held.name} cannot be used on register ${i + 1}`);
            }
        }
        for (const id of ids) {
            const held = (robot.options ?? []).find((o) => o.id === id);
            if (!held)
                problems.push(`no option ${id} to program`);
            else if (!isMoveOption(held))
                problems.push(`${id} is not a move option`);
        }
    });
    // And a gadget is not a move option: a move option deploys nothing.
    for (const ids of submission.gadgets ?? []) {
        for (const id of ids) {
            const held = (robot.options ?? []).find((o) => o.id === id);
            if (held && isMoveOption(held))
                problems.push(`${id} is a move option, not a gadget`);
            if (held && isPairingOption(held))
                problems.push(`${id} pairs cards, and is not a gadget`);
        }
    }
    // Turn mode's weapons: one a turn. Each fires instead of the main laser,
    // and Fire Control modifies it, so two switched on would be two answers
    // to the one question of what the robot fires.
    const weapons = (robot.options ?? []).filter((o) => (o.timing === 'turnProgrammed' && TURN_WEAPONS.has(o.effect)
        && (submission.activated ?? []).includes(o.id)));
    if (weapons.length > 1) {
        problems.push(`only one weapon can be chosen, not ${weapons.map((o) => o.id).join(' and ')}`);
    }
    // Pit Move and Shunt: one Option a push, so one of them a turn.
    const pushes = (robot.options ?? []).filter((o) => (o.timing === 'turnProgrammed' && PUSH_OPTIONS.has(o.effect)
        && (submission.activated ?? []).includes(o.id)));
    if (pushes.length > 1) {
        problems.push(`only one of ${pushes.map((o) => o.id).join(' and ')} can be on`);
    }
    // Aimed Options: the robot must hold the card, the card must be one
    // that turns, and it can only point one way at a time.
    const aimedAt = new Set();
    for (const { optionId, facing } of submission.aimed ?? []) {
        const held = (robot.options ?? []).find((o) => o.id === optionId);
        if (!held) {
            problems.push(`no option ${optionId} to aim`);
            continue;
        }
        // Turn mode's Transverse Beam and Shunt are set to a side.
        const sided = SIDED_TURN_CARDS.has(held.effect);
        if (!held.directional && !sided) {
            problems.push(`${optionId} is not an aimed option`);
            continue;
        }
        if (sided && facing !== 'left' && facing !== 'right') {
            problems.push(`${optionId} goes left or right`);
            continue;
        }
        if (!RELATIVE_FACINGS.includes(facing)) {
            problems.push(`${optionId} cannot face ${facing}`);
            continue;
        }
        if (aimedAt.has(optionId)) {
            problems.push(`${optionId} cannot face two ways at once`);
        }
        aimedAt.add(optionId);
    }
    // Cards set aside on Options come out of the hand like any other.
    for (const [i, entry] of (submission.setAside ?? []).entries()) {
        const { optionId, card } = entry;
        const holder = (robot.options ?? []).find((o) => o.id === optionId);
        const inHand = (robot.hand ?? []).some((c) => c.priority === card.priority);
        const alsoProgrammed = [
            ...(submission.registers ?? []), ...(submission.paired ?? []).map((p) => p?.card),
        ].some((c) => c?.priority === card.priority);
        // The same card cannot go on two Options, and one Option cannot take
        // two cards.
        const earlier = (submission.setAside ?? []).slice(0, i);
        const cardTwice = earlier.some((e) => e.card.priority === card.priority);
        const optionTwice = earlier.some((e) => e.optionId === optionId);
        if (!holder) {
            problems.push(`no option ${optionId} to set a card aside on`);
        }
        else if (holder.heldCard) {
            problems.push(`${optionId} is already holding a card`);
        }
        else if (optionTwice) {
            problems.push(`${optionId} cannot take two cards`);
        }
        else if (cardTwice) {
            problems.push('the same card cannot be set aside twice');
        }
        else if (!inHand) {
            problems.push('the card set aside is not in your hand');
        }
        else if (alsoProgrammed) {
            problems.push('a card cannot be both programmed and set aside');
        }
        else if (holder.effect === 'flywheel' && !MOVEMENT_CARDS.has(card.type)) {
            // "one of the remaining MOVEMENT cards" -- the Flywheel is specific
            // where the Conditional is not.
            problems.push('the flywheel takes a movement card');
        }
    }
    return problems;
}
/** A scrambled robot's submission, cut to what the bomb leaves its player:
 * no cards, no pairs and no gadgets, which are programming, and of the
 * rest only turn cards, whose originals are decided at run time. Every
 * other aim and switch is the one the bomb set at random. */
function scrambledChoices(r, sent) {
    const turnCards = new Set((r.options ?? []).filter((o) => o.turnMode).map((o) => o.id));
    // The bomb's settings, for the Options still held: one given up since
    // takes its setting with it.
    const held = new Set((r.options ?? []).map((o) => o.id));
    const bombed = (id) => held.has(id) && !turnCards.has(id);
    return {
        robotId: r.id,
        // Five: the bomb fills no register 6, so nothing there would play.
        registers: [null, null, null, null, null],
        ...(sent.moveOptions ? { moveOptions: sent.moveOptions.slice(0, 5).map((ids) => ids.filter((id) => turnCards.has(id))) } : {}),
        activated: [
            ...(r.activatedOptions ?? []).filter(bombed),
            ...(sent.activated ?? []).filter((id) => turnCards.has(id)),
        ],
        aimed: [
            ...(r.aimedOptions ?? []).filter((a) => bombed(a.optionId)),
            ...(sent.aimed ?? []).filter((a) => turnCards.has(a.optionId)),
        ],
    };
}
/** What a submission switches on, aims and programs into registers
 * besides cards, stored on the robot. Shared with a scrambled robot,
 * whose cards the bomb chose. */
function storeChoices(r, submission, events) {
    // Turn-programmed Options switched on for the turn. Filtered
    // against what the robot actually holds for the same reason the
    // gadget list is: a stale client should not be able to activate a
    // card that has since been lost or exchanged.
    if (submission.activated) {
        const held = new Set((r.options ?? [])
            .filter((o) => o.timing === 'turnProgrammed')
            .map((o) => o.id));
        const on = submission.activated.filter((id) => held.has(id));
        r.activatedOptions = on.length > 0 ? on : undefined;
        for (const id of on) {
            const card = (r.options ?? []).find((o) => o.id === id);
            events.push({
                type: 'optionActivated', robotId: r.id, name: card?.name ?? id,
            });
        }
    }
    // Move options, filtered against what the robot holds for the same reason.
    if (submission.moveOptions) {
        const held = new Set((r.options ?? []).filter(isMoveOption).map((o) => o.id));
        const moveOptions = submission.registers.map((_, i) => (submission.moveOptions[i] ?? []).filter((id) => held.has(id)));
        r.moveOptions = moveOptions.some((ids) => ids.length > 0) ? moveOptions : undefined;
    }
    // Aimed Options. Filtered against what the robot holds for the same
    // reason, and a card left out is not defaulted to front here: front
    // is what `aimedSideOf` answers for an Option with no entry, so
    // writing one down would only be a second place to keep it right.
    if (submission.aimed) {
        const turns = (r.options ?? []).filter((o) => o.directional || SIDED_TURN_CARDS.has(o.effect));
        const byId = new Map(turns.map((o) => [o.id, o]));
        const aimed = submission.aimed.filter((a) => byId.has(a.optionId));
        r.aimedOptions = aimed.length > 0 ? aimed : undefined;
        for (const { optionId, facing } of aimed) {
            events.push({
                type: 'optionAimed', robotId: r.id,
                name: byId.get(optionId)?.name ?? optionId, side: facing,
            });
        }
    }
}
/**
 * Applies every player's submission. Call once at the close of the Program
 * step with `timerExpired` set to whether the On the Clock timer ran out.
 *
 * A robot whose registers all end up filled has its leftover hand
 * discarded and its hand cleared -- its programming is final. A robot left
 * with an empty register (only possible while the timer is still running)
 * keeps its hand so the player can submit again, and is named in
 * `incomplete`.
 *
 * A submission that fails validation leaves that robot completely
 * untouched -- registers and hand both -- and is named in `rejected` so the
 * server can ask again. It is not treated as a submission of nothing,
 * which would quietly cost the player their turn over what is most likely
 * a client bug.
 */
export function resolveProgram(robots, deck, submissions, rng, options = {}) {
    const timerExpired = options.timerExpired ?? false;
    const byRobot = new Map(submissions.map((s) => [s.robotId, s]));
    const working = robots.map((r) => ({ ...r }));
    const events = [];
    const rejected = [];
    const incomplete = [];
    let currentDeck = deck;
    for (const r of working) {
        if (r.destroyed || r.poweredDown)
            continue;
        // A robot a scrambler bomb caught is already programmed: the bomb
        // filled its registers at random at the Deal and took its hand. In
        // turn mode its player still submits: the power-down answer, and the
        // turn cards whose originals are run-time choices, which do not need
        // to see the cards. Its cards are never changed.
        // The aims and switches the bomb set at random are stored again with
        // them, so they are logged as programming closes, as anyone's are.
        if (r.scrambledThisTurn === true) {
            const sent = (options.turnMode ? byRobot.get(r.id) : undefined)
                ?? { robotId: r.id, registers: [null, null, null, null, null] };
            const kept = scrambledChoices(r, sent);
            // Checked as a program would be with every register locked:
            // nothing the bomb put there is the player's to change. A facing,
            // power choice or Option still owed is settled by its own answer,
            // or by the clock's default, and never waits on this.
            const problems = validateSubmission({
                ...r, hand: [], lockedRegisters: kept.registers.map(() => true),
                owesFacing: undefined, owesPowerChoice: undefined, owesOptionLoss: undefined,
            }, kept, options.turnNumber);
            if (problems.length > 0)
                rejected.push({ robotId: r.id, problems });
            else
                storeChoices(r, kept, events);
            continue;
        }
        // Not yet programming: it has still to say whether it is powered down,
        // and powers down if it never does.
        if (r.owesPowerChoice)
            continue;
        const submission = byRobot.get(r.id);
        if (submission) {
            const problems = validateSubmission(r, submission, options.turnNumber);
            if (problems.length > 0) {
                rejected.push({ robotId: r.id, problems });
                continue;
            }
        }
        // A submitted card names a card in the hand by its priority, which
        // validation checked; the hand's copy is what is stored, so a card
        // sent with another type plays as the card the robot holds.
        const fromHand = (card) => ((r.hand ?? []).find((c) => c.priority === card.priority) ?? card);
        // Validated inside validateSubmission, so a bad one is rejected with
        // everything else rather than half-applied.
        const setAside = (submission?.setAside ?? []).map((e) => ({ ...e, card: fromHand(e.card) }));
        const locked = r.lockedRegisters ?? [false, false, false, false, false];
        const registers = (r.registers ?? [null, null, null, null, null]).slice();
        while (registers.length < 5)
            registers.push(null);
        let hand = (r.hand ?? []).slice();
        if (submission) {
            // NOT `turnTo()`. Setting a starting facing in the docking bay is
            // not a robot turning on the board: nothing has happened in play
            // yet, and `rotated` would put a phantom turn in the register log.
            if (submission.facing) {
                r.facing = submission.facing;
                events.push({ type: 'facingSet', robotId: r.id });
            }
            submission.registers.forEach((card, i) => {
                if (locked[i])
                    return;
                const pair = submission.paired?.[i] ?? null;
                const cards = [card, pair?.card].filter((c) => c != null).map(fromHand);
                // An Overload Override register left empty is programmed: it holds
                // the Option and no card, and the timer leaves it alone.
                if (cards.length === 0 && !pair)
                    return;
                registers[i] = pair ? { ...slotOfMany(cards), via: pair.via } : slotOf(cards[0]);
                hand = hand.filter((c) => !cards.some((p) => p.priority === c.priority));
            });
            // Gadgets attach to the register they were programmed into. A
            // register can carry one with no card at all -- dropping something
            // and standing still is a legitimate play.
            if (submission.gadgets) {
                const held = new Set((r.options ?? []).map((o) => o.id));
                submission.gadgets.forEach((ids, i) => {
                    // One per register: a robot deploys one thing at a time. Taking
                    // the first rather than rejecting keeps a stale client from
                    // being unable to program at all; validateSubmission rejects it
                    // properly for a client that should know better.
                    const valid = ids.filter((id) => held.has(id)).slice(0, 1);
                    if (valid.length === 0)
                        return;
                    const slot = registers[i];
                    registers[i] = slot
                        ? { ...slot, gadgets: valid }
                        : { cards: [], gadgets: valid };
                });
            }
            storeChoices(r, submission, events);
            events.push({ type: 'programmed', robotId: r.id });
        }
        // Timer expiry: the computer fills what the player didn't, drawing at
        // random from that player's own remaining hand.
        //
        // What needs filling is a register with no CARD, which is not the same
        // as an empty SLOT: a gadget programmed into a register the player had
        // not yet put a card in leaves `{ cards: [], gadgets: [...] }` there,
        // and testing the slot for null skipped it -- so a player who armed a
        // dropper and ran out of time got a register that did nothing but
        // drop.
        //
        // And filled through `reprogram`, which keeps the gadget: `slotOf`
        // builds a slot from the card alone, so the auto-fill was the sixth
        // site to disarm a launcher while replacing a card. The other five are
        // named at `reprogram` itself.
        if (timerExpired) {
            const empty = registers
                .map((slot, i) => (!locked[i] && (slot === null || (slot.cards.length === 0 && !slot.via)) ? i : -1))
                // Never register 6: left empty, it is not played.
                .filter((i) => i >= 0 && i < 5);
            if (empty.length > 0) {
                // A card set aside on an Option is NOT one of the remaining cards
                // -- it is on the table, face down, and both cards that hold one
                // say "one of the remaining program cards". The set-aside is
                // applied below, after this, so the hand still holds it here: the
                // fill could deal the very card the player had put on their
                // Conditional, leaving it in a register AND on the Option.
                const aside = new Set(setAside.map((e) => e.card.priority));
                const pool = shuffle(hand.filter((c) => !aside.has(c.priority)), rng);
                for (const i of empty) {
                    const card = pool.shift();
                    if (!card)
                        break; // hand exhausted -- reported via `incomplete` below
                    registers[i] = reprogram(registers[i] ?? null, card);
                    hand = hand.filter((c) => c.priority !== card.priority);
                    events.push({ type: 'autoFilled', robotId: r.id, count: i + 1 });
                }
            }
        }
        r.registers = registers;
        // Cards set aside on Options come off the hand and onto the Option.
        //
        // BEFORE the leftovers are discarded, and for every submission, not
        // only incomplete ones. This block used to sit inside the `else`
        // below, so it ran only when the player had NOT filled all five
        // registers -- which is to say, essentially never. A complete program
        // with a card on the Conditional set nothing aside and discarded the
        // card as a leftover, so the player was never offered it at reveal
        // and had no idea why.
        for (const entry of setAside) {
            const onto = (r.options ?? []).find((o) => o.id === entry.optionId);
            r.options = (r.options ?? []).map((o) => (o.id === entry.optionId ? { ...o, heldCard: entry.card } : o));
            hand = hand.filter((c) => c.priority !== entry.card.priority);
            events.push({
                type: 'cardSetAside', robotId: r.id,
                // The Option's own name from the catalog, not its id and not a
                // label kept somewhere else: a second copy of these names would
                // only drift from the catalog, which is what it did.
                //
                // WHICH Option, never WHICH CARD. That a card is face down on an
                // Option is public -- it sits on the table -- but the card is a
                // card out of a hand, and the log is one feed shared by the whole
                // table. A Conditional's is named by `conditionalUsed` when it is
                // swapped into a register; a Flywheel's is never named, because
                // it goes back into a hand and is only ever seen as it plays.
                name: onto?.name ?? entry.optionId,
            });
        }
        // A register is filled when it holds a card, or is an Overload
        // Override register left empty on purpose. A gadget alone is not a
        // program: it deploys at its card's priority and has none.
        const allFilled = registers.every((s) => s !== null && (s.cards.length > 0 || s.via !== undefined));
        if (allFilled) {
            if (hand.length > 0) {
                currentDeck = discardCards(currentDeck, hand);
                events.push({ type: 'leftoversDiscarded', robotId: r.id, count: hand.length });
            }
            r.hand = [];
        }
        else {
            r.hand = hand; // keep it -- the player can still submit
            incomplete.push(r.id);
        }
    }
    return { robots: working, deck: currentDeck, events, rejected, incomplete };
}
/**
 * Recompile: throw the hand away, take a fresh one, and pay a point of
 * damage for it.
 *
 * "You may receive a new hand of cards once per turn before your robot is
 * programmed. Your robot takes a point of damage after you receive the
 * new hand. (This point of damage does not require you to discard a
 * card.)"
 *
 * It lives in the Program step rather than at a register: the card is
 * marked Run-Time, but the moment it names is BEFORE programming, so it
 * is a decision the player makes on the programming screen and not a
 * prompt raised as a register runs. Nothing in `runTimeChoiceApplies`
 * knows about it.
 *
 * The new hand is the same SIZE as the one it replaces rather than being
 * recomputed from damage. That is what "does not require you to discard a
 * card" says -- the point taken afterwards must not shrink the hand it
 * paid for -- and it carries Extra Memory's extra card and a card a
 * Flywheel has already handed over without either being consulted again.
 *
 * The old hand goes to the discard pile, so a deck too short to deal a
 * whole new hand reshuffles and deals it anyway. Nothing is lost: the
 * deck is rebuilt from scratch every Deal.
 *
 * Refuses rather than half-acts, and says why: the caller turns the
 * problems into a rejection the player can see.
 */
export function resolveRecompile(robots, deck, robotId, rng) {
    const problems = [];
    const target = robots.find((r) => r.id === robotId);
    if (!target)
        problems.push(`no robot ${robotId}`);
    else if (target.destroyed || target.eliminated)
        problems.push('this robot is out of play');
    else if (target.poweredDown)
        problems.push('a powered-down robot has no hand to replace');
    else if (!holdsOption(target.options, 'recompile'))
        problems.push('this robot has no Recompile');
    else if (target.recompiledThisTurn)
        problems.push('Recompile has already been used this turn');
    // The bomb took the hand and chose the cards; a new hand has nowhere to go.
    else if (target.scrambledThisTurn)
        problems.push('a scrambled robot has no hand to replace');
    if (problems.length > 0) {
        return { robots, deck, events: [], rejected: [{ robotId, problems }] };
    }
    const events = [];
    const holder = target;
    const old = holder.hand ?? [];
    const drawn = drawCards(discardCards(deck, old), old.length, rng);
    // A copy, so the damage below lands on the robot going back rather than
    // on the one still in `robots`.
    const fresh = {
        ...holder, hand: drawn.cards, recompiledThisTurn: true,
    };
    events.push({ type: 'recompiled', robotId, count: drawn.cards.length });
    // Through `hurt` like every other point in the game, so an Ablative
    // Coat soaks it up and 10 points destroys the robot. No side is given:
    // the damage comes from the robot's own card, not from a direction, so
    // a Power Down Shield has nothing to stop -- and a powered-down robot
    // cannot get here anyway.
    hurt(fresh, 1, events, { type: 'recompileDamage' });
    return {
        robots: robots.map((r) => (r.id === robotId ? fresh : r)),
        deck: drawn.deck,
        events,
        rejected: [],
    };
}
